import { Result } from './result';
import { assertNever } from './errors';

export enum CancellableStatus {
    Pending,
    Running,
    Success,
    Failure,
    UnexpectedError,
    Cancelled,
}

export interface BaseCancellableResult {
    _isCancellableResult: true;
}

export interface CancellableResultSuccess<TValue> extends BaseCancellableResult {
    status: CancellableStatus.Success;
    value: TValue;
}

export interface CancellableResultFailure<TFailure> extends BaseCancellableResult {
    status: CancellableStatus.Failure;
    failure: TFailure;
}

export interface CancellableResultUnexpectedError extends BaseCancellableResult {
    status: CancellableStatus.UnexpectedError;
    error: any;
}

export interface CancellableResultCancelled extends BaseCancellableResult {
    status: CancellableStatus.Cancelled;
}

export type CancellableResult<TFailure, TValue> =
    | CancellableResultCancelled
    | CancellableResultFailure<TFailure>
    | CancellableResultSuccess<TValue>
    | CancellableResultUnexpectedError;

export function isResult<TFailure, TValue>(
    result: CancellableResult<TFailure, TValue> | Result<TFailure, TValue>,
): result is Result<TFailure, TValue> {

    return (result as Result<TFailure, TValue>).success !== undefined;
}

export class CancellableResultFactory {
    public success<TValue>(value: TValue): CancellableResultSuccess<TValue> {
        return {
            _isCancellableResult: true,
            status: CancellableStatus.Success,
            value,
        };
    }

    public failure<TFailure>(failure: TFailure): CancellableResultFailure<TFailure> {
        return {
            _isCancellableResult: true,
            failure,
            status: CancellableStatus.Failure,
        };
    }

    public error(error: any): CancellableResultUnexpectedError {
        return {
            _isCancellableResult: true,
            error,
            status: CancellableStatus.UnexpectedError,
        };
    }

    public cancelled(): CancellableResultCancelled {
        return {
            _isCancellableResult: true,
            status: CancellableStatus.Cancelled,
        };
    }
}

export const cancellableResultFactory = new CancellableResultFactory();

export abstract class CancellableFuture<TFailure, TValue> {
    protected status: CancellableStatus = CancellableStatus.Pending;

    protected _promise: Promise<CancellableResult<TFailure, TValue>> | undefined;

    /**
     * Getting the promise will immediately trigger the future, if it has not already started.
     *
     * @returns A promise
     */
    public get promise(): Promise<CancellableResult<TFailure, TValue>> {
        if (this._promise) {
            return this._promise;
        } else {
            return this.run();
        }
    }

    /**
     * Implement this method to perform your cancellation.
     */
    protected abstract _cancel(): Promise<void>;

    /**
     * This is the main body of your action. It is async. It can return either a "Result" (success/failure), or
     * "CancellableResult" (success/failure/cancelled/unexpected error).
     */
    protected abstract _run(): Promise<CancellableResult<TFailure, TValue> | Result<TFailure, TValue>>;

    public cancel = async (): Promise<void> => {
        this.status = CancellableStatus.Cancelled;
        return this._cancel();
    };

    protected async run(): Promise<CancellableResult<TFailure, TValue>> {
        this.status = CancellableStatus.Running;
        this._promise = this._run().then(
            result => {
                if (this.status === CancellableStatus.Cancelled) {
                    return cancellableResultFactory.cancelled();
                } else if (isResult(result)) {
                    // Wrap Result objects
                    switch (result.success) {
                        case true:
                            this.status = CancellableStatus.Success;
                            return cancellableResultFactory.success(result.value);
                        case false:
                            this.status = CancellableStatus.Failure;
                            return cancellableResultFactory.failure(result.failure);
                        default:
                            throw assertNever(result);
                    }
                } else {
                    return result; // Chain CancellableResult objects
                }
            },
            err => {
                this.status = CancellableStatus.UnexpectedError;
                return cancellableResultFactory.error(err);
            },
        );
        return this._promise;
    }
}

export function isCancellableResult<TFailure, TValue>(
    // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
    result: CancellableResult<TFailure, TValue> | any,
): result is CancellableResult<TFailure, TValue> {
    // Noinspection PointlessBooleanExpressionJS
    return !!result._isCancellableResult;
}
